<template>
    <div class="best-build">
        <header class="best-build__header">
            <div class="best-build__title">
                <span>Survivor build lab</span>
                <h1>Best Build</h1>
            </div>
            <div class="best-build__controls">
                <label class="best-build__control">
                    <span>Central perk</span>
                    <select v-model="centralPerkId">
                        <option v-for="perk in centralPerkOptions" :key="perk.id" :value="perk.id">
                            {{ perkDisplayName(perk) }}
                        </option>
                    </select>
                </label>
                <button type="button" class="best-build__button" @click="randomCentral">Random central</button>
                <button type="button" class="best-build__button" @click="resetLocks">Reset locks</button>
            </div>
        </header>

        <section class="best-build__mode-switch" aria-label="Build mode">
            <button
                v-for="modeOption in buildModeOptions"
                :key="modeOption.id"
                type="button"
                :class="['best-build__mode-button', { 'best-build__mode-button--active': buildMode === modeOption.id }]"
                @click="selectBuildMode(modeOption.id)"
            >
                <span>{{ modeOption.kicker }}</span>
                <strong>{{ modeOption.label }}</strong>
                <small>{{ modeOption.description }}</small>
            </button>
        </section>

        <section v-if="bestBuild.status === 'success'" class="best-build__summary">
            <div class="best-build__summary-copy">
                <span class="best-build__eyebrow">{{ selectedBuildMode.label }}</span>
                <strong>{{ bestBuild.variant.title }}</strong>
                <span>{{ bestBuild.variant.summary }}</span>
                <div class="best-build__tradeoffs">
                    <span v-if="bestBuild.variant.advantages && bestBuild.variant.advantages.length" class="is-positive">
                        + {{ bestBuild.variant.advantages[0] }}
                    </span>
                    <span v-if="bestBuild.variant.drawbacks && bestBuild.variant.drawbacks.length" class="is-negative">
                        &minus; {{ bestBuild.variant.drawbacks[0] }}
                    </span>
                </div>
                <small class="best-build__disclaimer">Compatibility is an estimate, not a verdict: preference and match context still matter.</small>
            </div>
            <div class="best-build__summary-stats">
                <span>
                    <small>Central</small>
                    <strong>{{ perkDisplayName(bestBuild.slots[0].perk) }}</strong>
                </span>
                <span>
                    <small>Perk strength avg</small>
                    <strong>{{ currentBuildPowerAverage }}/100</strong>
                </span>
                <span>
                    <small>Compatibility</small>
                    <strong>{{ currentBuildCompatibilityAverage }}/100</strong>
                </span>
                <span>
                    <small>Locks</small>
                    <strong>{{ activeLockCount }}/3</strong>
                </span>
            </div>
            <span class="best-build__source">{{ bestBuild.planSource }}</span>
        </section>

        <section v-if="bestBuild.status === 'success' && variantOptions.length > 1" class="best-build__plan-row">
            <button
                v-for="variant in variantOptions"
                :key="variant.id"
                type="button"
                :class="['best-build__plan-button', { 'best-build__plan-button--active': bestBuild.variant.id === variant.id }]"
                @click="selectVariant(variant.id)"
            >
                <strong>{{ variant.title }}</strong>
                <span>{{ variant.summary }}</span>
            </button>
        </section>

        <section v-if="bestBuild.status === 'success'" class="best-build__grid">
            <article
                v-for="slot in bestBuild.slots"
                :key="slot.slot"
                :class="['build-slot', { 'build-slot--central': slot.slot === 1, 'build-slot--locked': isSlotLocked(slot.slot) }]"
            >
                <div class="build-slot__topline">
                    <span>Slot {{ slot.slot }}</span>
                    <strong>{{ slot.label }}</strong>
                </div>
                <div class="build-slot__icon">
                    <PerkIcon :perk="slot.perk" side="survivor" class="build-slot__sprite" />
                </div>
                <h2>{{ perkDisplayName(slot.perk) }}</h2>
                <p>{{ slot.note || slotDescription(slot) }}</p>
                <div class="build-slot__meta">
                    <span>{{ compatibilityLabel(slot.compatibility) }}</span>
                    <div class="build-slot__meta-values">
                        <span :class="['build-slot__tier', 'build-slot__tier--' + perkStrengthTier(slot.perk).toLowerCase()]">
                            {{ perkStrengthTier(slot.perk) }}
                        </span>
                        <strong v-if="slot.slot !== 1">{{ slot.score }}</strong>
                        <strong v-else>Central</strong>
                    </div>
                </div>
                <div v-if="slot.slot !== 1" class="build-slot__actions">
                    <button
                        type="button"
                        :class="['build-slot__lock-button', { 'build-slot__lock-button--active': isSlotLocked(slot.slot) }]"
                        @click="toggleSlotLock(slot.slot, slot.perkId, !isSlotLocked(slot.slot))"
                    >
                        {{ isSlotLocked(slot.slot) ? 'Locked' : 'Lock slot' }}
                    </button>
                    <label v-if="isLoadoutCraft" class="build-slot__search">
                        <span>Search all perks</span>
                        <input
                            type="search"
                            :value="slotSearchQuery(slot.slot)"
                            placeholder="Type a perk name..."
                            @input="setSlotSearchQuery(slot.slot, $event.target.value)"
                        >
                        <small>{{ visibleAlternatives(slot).length }}/{{ slotAlternativeOptions(slot).length }}</small>
                    </label>
                    <div :class="['build-slot__alternatives', { 'build-slot__alternatives--scroll': isLoadoutCraft }]">
                        <button
                            v-for="option in visibleAlternatives(slot)"
                            :key="option.perkId"
                            type="button"
                            :title="compatibilityReason(option.compatibility)"
                            @click="selectAlternative(slot.slot, option.perkId)"
                        >
                            <span class="build-slot__alternative-copy">
                                <span>{{ perkNameById(option.perkId) }}</span>
                                <small v-if="option.compatibility">
                                    {{ compatibilityLabel(option.compatibility) }} &middot; {{ option.compatibility.score }}/100
                                </small>
                            </span>
                            <span class="build-slot__alternative-meta">
                                <span :class="['build-slot__tier', 'build-slot__tier--' + perkStrengthTierById(option.perkId).toLowerCase()]">
                                    {{ perkStrengthTierById(option.perkId) }}
                                </span>
                                <strong :class="{ 'build-slot__score--zero': option.score === 0 }">{{ option.score }}</strong>
                            </span>
                        </button>
                        <span v-if="!visibleAlternatives(slot).length" class="build-slot__empty-alternatives">No clean alternative</span>
                    </div>
                </div>
            </article>
        </section>

        <section v-if="bestBuild.status === 'success'" class="best-build__funnel">
            <div v-for="slot in bestBuild.slots" :key="'funnel-' + slot.slot">
                <span>Slot {{ slot.slot }}</span>
                <strong>{{ perkDisplayName(slot.perk) }}</strong>
            </div>
        </section>

        <section v-if="bestBuild.status === 'success' && bestBuild.warnings.length" class="best-build__warnings">
            <strong>Rules</strong>
            <ul>
                <li v-for="warning in bestBuild.warnings" :key="warning">{{ warning }}</li>
            </ul>
        </section>

        <section v-if="bestBuild.status === 'success'" class="best-build__details">
            <div class="best-build__details-header">
                <div>
                    <strong>Current build</strong>
                    <span>{{ buildLine }}</span>
                </div>
                <div>
                    <strong>Locks</strong>
                    <span>{{ activeLockCount }}/3 satellite slots</span>
                </div>
                <div>
                    <strong>Perk strength</strong>
                    <span>Avg {{ currentBuildPowerAverage }}/100 &middot; {{ currentBuildTierSummary }}</span>
                </div>
            </div>
            <div class="best-build__perk-details">
                <article
                    v-for="entry in currentBuildDetails"
                    :key="'detail-' + entry.slot.slot"
                    class="best-build__perk-detail"
                >
                    <div class="best-build__perk-detail-icon">
                        <PerkIcon :perk="entry.slot.perk" side="survivor" />
                    </div>
                    <div class="best-build__perk-detail-body">
                        <span>Slot {{ entry.slot.slot }} / {{ entry.slot.label }}</span>
                        <strong>{{ perkDisplayName(entry.slot.perk) }}</strong>
                        <div class="best-build__perk-detail-meta">
                            <span :class="['build-slot__tier', 'build-slot__tier--' + perkStrengthTier(entry.slot.perk).toLowerCase()]">
                                Tier {{ perkStrengthTier(entry.slot.perk) }}
                            </span>
                            <span>Power {{ perkStrengthScore(entry.slot.perk) }}/100</span>
                        </div>
                        <ul>
                            <li
                                v-for="(line, index) in entry.lines"
                                :key="'detail-line-' + entry.slot.slot + '-' + index"
                            >
                                {{ line }}
                            </li>
                        </ul>
                    </div>
                </article>
            </div>
        </section>
    </div>
</template>

<script>
import PerkIcon from '@/components/PerkIcon.vue'
import generateSurvivorBestBuild, { BEST_BUILD_MODES, getSurvivorBestBuildPlan } from '@/lib/buildGenerator/generateSurvivorBestBuild'
import { createSurvivorCatalog } from '@/data/survivorPerks'
import { localizedEntryName } from '@/lib/loadoutLocalization'
import { officialEntryProfile } from '@/lib/loadoutOfficialDescriptions'

const BEST_BUILD_MODE_STORAGE_KEY = 'dbd-survivor-best-build-mode'
const BUILD_MODE_OPTIONS = [
  {
    id: BEST_BUILD_MODES.loadoutCraft,
    kicker: 'Cook a build',
    label: 'Loadout Craft',
    description: 'Complete builds with a clear job for each satellite slot.'
  },
  {
    id: BEST_BUILD_MODES.synergyFocus,
    kicker: 'Push the combo',
    label: 'Synergy Focus',
    description: 'Maximize the central perk and the previous slot picks.'
  }
]

function getValidBuildMode (mode) {
  return BUILD_MODE_OPTIONS.some(option => option.id === mode) ? mode : null
}

function getStoredBuildMode () {
  if (typeof window === 'undefined' || !window.localStorage) return null
  return getValidBuildMode(window.localStorage.getItem(BEST_BUILD_MODE_STORAGE_KEY))
}

export default {
  name: 'SurvivorBestBuild',
  components: { PerkIcon },
  props: {
    lang: {
      type: String,
      required: true
    },
    color: {
      type: Boolean,
      default: false,
      required: false
    },
    sids: {
      type: Array,
      default: function () {
        return []
      },
      required: false
    },
    kids: {
      type: Array,
      default: function () {
        return []
      },
      required: false
    },
    perksSHD: {
      type: Object,
      required: true
    }
  },
  data: function () {
    const survivorFrames = Object.keys(this.perksSHD.frames)
    const survivors = []
    for (let i = 0, sLen = survivorFrames.length; i < sLen; i++) {
      const perkFileName = survivorFrames[i]
      const key = Number(perkFileName.replace(/\D+$/g, ''))
      survivors[key] = {
        index: key,
        name: perkFileName
      }
    }
    const buildCatalog = createSurvivorCatalog(survivors)
    const initialCentral = buildCatalog.find(perk => perk.id === this.$route.query.central) ||
      buildCatalog.find(perk => perk.id === 'extrasensory_perception') ||
      buildCatalog[0]
    const initialBuildMode = getValidBuildMode(this.$route.query.mode) ||
      getStoredBuildMode() ||
      BEST_BUILD_MODES.loadoutCraft

    return {
      perkData: survivors,
      buildCatalog: buildCatalog,
      centralPerkId: initialCentral.id,
      buildMode: initialBuildMode,
      variantId: null,
      slotLocks: {
        2: { enabled: false, perkId: null },
        3: { enabled: false, perkId: null },
        4: { enabled: false, perkId: null }
      },
      slotSearchQueries: {
        2: '',
        3: '',
        4: ''
      }
    }
  },
  computed: {
    centralPerkOptions () {
      return this.buildCatalog.slice().sort((first, second) => {
        return this.perkDisplayName(first).localeCompare(this.perkDisplayName(second))
      })
    },
    currentPlan () {
      return getSurvivorBestBuildPlan(this.buildCatalog, this.centralPerkId)
    },
    buildModeOptions () {
      return BUILD_MODE_OPTIONS
    },
    selectedBuildMode () {
      return BUILD_MODE_OPTIONS.find(option => option.id === this.buildMode) || BUILD_MODE_OPTIONS[0]
    },
    isLoadoutCraft () {
      return this.buildMode === BEST_BUILD_MODES.loadoutCraft
    },
    variantOptions () {
      const options = this.currentPlan.variants.filter(variant => {
        return (variant.mode || BEST_BUILD_MODES.loadoutCraft) === this.buildMode
      })
      return options.length ? options : this.currentPlan.variants
    },
    bestBuild () {
      return generateSurvivorBestBuild(this.buildCatalog, {
        centralPerkId: this.centralPerkId,
        mode: this.buildMode,
        variantId: this.variantId,
        lockedSlots: this.slotLocks
      })
    },
    buildLine () {
      if (this.bestBuild.status !== 'success') return ''
      return this.bestBuild.slots.map(slot => this.perkDisplayName(slot.perk)).join(' / ')
    },
    currentBuildPowerAverage () {
      if (this.bestBuild.status !== 'success') return 0
      const total = this.bestBuild.slots.reduce((sum, slot) => sum + this.perkStrengthScore(slot.perk), 0)
      return Math.round(total / this.bestBuild.slots.length)
    },
    currentBuildCompatibilityAverage () {
      if (this.bestBuild.status !== 'success') return 0
      const satellites = this.bestBuild.slots.filter(slot => slot.slot !== 1 && slot.compatibility)
      if (!satellites.length) return 0
      const total = satellites.reduce((sum, slot) => sum + slot.compatibility.score, 0)
      return Math.round(total / satellites.length)
    },
    currentBuildTierSummary () {
      if (this.bestBuild.status !== 'success') return ''
      return this.bestBuild.slots.map(slot => this.perkStrengthTier(slot.perk)).join('/')
    },
    currentBuildDetails () {
      if (this.bestBuild.status !== 'success') return []
      return this.bestBuild.slots.map(slot => {
        const profile = officialEntryProfile(slot.perk, 'perk', 'survivor', this.$i18n.locale)
        const description = profile.description
        return {
          slot,
          lines: description.split(/\n+/).filter(Boolean)
        }
      })
    },
    activeLockCount () {
      return this.satelliteSlots.filter(slot => this.slotLocks[slot] && this.slotLocks[slot].enabled).length
    },
    satelliteSlots () {
      return [2, 3, 4]
    }
  },
  watch: {
    centralPerkId: function () {
      this.resetLocks()
      this.variantId = this.defaultVariantId()
      this.syncRouteQuery({
        central: this.centralPerkId,
        mode: this.buildMode,
        plan: this.variantId
      })
    },
    buildMode: function () {
      this.persistBuildMode()
      this.clearSlotSearches()
      this.variantId = this.defaultVariantId()
      this.syncRouteQuery({
        central: this.centralPerkId,
        mode: this.buildMode,
        plan: this.variantId
      })
    },
    variantId: function () {
      this.syncRouteQuery({
        central: this.centralPerkId,
        mode: this.buildMode,
        plan: this.variantId
      })
    },
    variantOptions: function () {
      if (!this.variantOptions.find(variant => variant.id === this.variantId)) {
        this.variantId = this.defaultVariantId()
      }
    }
  },
  mounted: function () {
    const routeVariant = this.$route.query.plan
    this.variantId = this.variantOptions.find(variant => variant.id === routeVariant)
      ? routeVariant
      : this.defaultVariantId()
    this.syncRouteQuery({
      central: this.centralPerkId,
      mode: this.buildMode,
      plan: this.variantId
    })
  },
  methods: {
    defaultVariantId: function () {
      const plan = this.currentPlan || getSurvivorBestBuildPlan(this.buildCatalog, this.centralPerkId)
      const modeDefault = plan.defaultVariants && plan.defaultVariants[this.buildMode]
      if (modeDefault && this.variantOptions.some(variant => variant.id === modeDefault)) return modeDefault
      return (this.variantOptions[0] && this.variantOptions[0].id) || plan.defaultVariant
    },
    perkDisplayName: function (perk) {
      if (!perk) return ''
      return localizedEntryName(perk, 'perk', this.$i18n.locale)
    },
    perkNameById: function (perkId) {
      const perk = this.buildCatalog.find(candidate => candidate.id === perkId)
      return perk ? this.perkDisplayName(perk) : perkId
    },
    perkStrengthScore: function (perk) {
      return perk && typeof perk.strengthScore === 'number' ? perk.strengthScore : 50
    },
    perkStrengthTier: function (perk) {
      return perk && perk.strengthTier ? perk.strengthTier : 'C'
    },
    perkStrengthTierById: function (perkId) {
      const perk = this.buildCatalog.find(candidate => candidate.id === perkId)
      return this.perkStrengthTier(perk)
    },
    randomCentral: function () {
      const centralPool = this.buildCatalog.filter(perk => perk.centralEligible)
      const selected = centralPool[Math.floor(Math.random() * centralPool.length)]
      if (!selected) return
      this.centralPerkId = selected.id
    },
    resetLocks: function () {
      this.satelliteSlots.forEach(slot => {
        this.$set(this.slotLocks, slot, { enabled: false, perkId: null })
      })
      this.clearSlotSearches()
    },
    selectBuildMode: function (mode) {
      const validMode = getValidBuildMode(mode)
      if (!validMode || validMode === this.buildMode) return
      this.buildMode = validMode
    },
    persistBuildMode: function () {
      if (typeof window === 'undefined' || !window.localStorage) return
      window.localStorage.setItem(BEST_BUILD_MODE_STORAGE_KEY, this.buildMode)
    },
    isSlotLocked: function (slot) {
      return Boolean(this.slotLocks[slot] && this.slotLocks[slot].enabled)
    },
    toggleSlotLock: function (slot, perkId, enabled) {
      this.$set(this.slotLocks, slot, {
        enabled: enabled,
        perkId: enabled ? perkId : null
      })
    },
    selectAlternative: function (slot, perkId) {
      const matchingVariant = this.isLoadoutCraft ? null : this.findVariantForSlot(slot, perkId)
      if (matchingVariant) this.variantId = matchingVariant.id
      this.$set(this.slotLocks, slot, {
        enabled: true,
        perkId: perkId
      })
      this.$set(this.slotSearchQueries, slot, '')
    },
    selectVariant: function (variantId) {
      this.variantId = variantId
    },
    syncRouteQuery: function (nextValues) {
      const nextQuery = Object.assign({}, this.$route.query, nextValues)
      Object.keys(nextQuery).forEach(key => {
        if (nextQuery[key] === null || typeof nextQuery[key] === 'undefined' || nextQuery[key] === '') {
          delete nextQuery[key]
        }
      })

      const currentKeys = Object.keys(this.$route.query).sort()
      const nextKeys = Object.keys(nextQuery).sort()
      const isSameQuery = currentKeys.length === nextKeys.length && nextKeys.every(key => {
        return String(this.$route.query[key]) === String(nextQuery[key])
      })
      if (isSameQuery) return

      this.$router.replace({
        name: this.$route.name,
        query: nextQuery
      }).catch(() => {})
    },
    findVariantForSlot: function (slot, perkId) {
      if (this.isLoadoutCraft) return null
      const slotIndex = this.satelliteSlots.indexOf(slot)
      return this.variantOptions.find(variant => {
        const variantSlot = variant.slots[slotIndex]
        return Boolean(variantSlot && variantSlot.options.some(option => option.perkId === perkId))
      })
    },
    slotSearchQuery: function (slot) {
      return this.slotSearchQueries[slot] || ''
    },
    setSlotSearchQuery: function (slot, value) {
      this.$set(this.slotSearchQueries, slot, value)
    },
    clearSlotSearches: function () {
      this.satelliteSlots.forEach(slot => {
        this.$set(this.slotSearchQueries, slot, '')
      })
    },
    normalizeSearchText: function (value) {
      return String(value || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    },
    slotAlternativeOptions: function (slot) {
      const selectedOtherPerkIds = this.bestBuild.status === 'success'
        ? this.bestBuild.slots
          .filter(selectedSlot => selectedSlot.slot !== slot.slot)
          .map(selectedSlot => selectedSlot.perkId)
        : []
      const selectedOtherSet = new Set(selectedOtherPerkIds)
      const options = slot.alternatives || []
      const seen = new Set()
      return options.filter(option => {
        if (seen.has(option.perkId)) return false
        if (selectedOtherSet.has(option.perkId)) return false
        if (option.perkId === slot.perkId) return false
        seen.add(option.perkId)
        return true
      })
    },
    visibleAlternatives: function (slot) {
      const options = this.slotAlternativeOptions(slot)
      if (!this.isLoadoutCraft) return options.slice(0, 5)

      const query = this.normalizeSearchText(this.slotSearchQuery(slot.slot).trim())
      if (!query) return options

      return options
        .filter(option => {
          const perk = this.buildCatalog.find(candidate => candidate.id === option.perkId)
          const displayName = perk ? this.perkDisplayName(perk) : option.perkId
          return this.normalizeSearchText(displayName).indexOf(query) !== -1 ||
            this.normalizeSearchText(option.perkId).indexOf(query) !== -1
        })
        .sort((first, second) => {
          return this.perkNameById(first.perkId).localeCompare(this.perkNameById(second.perkId))
        })
    },
    compatibilityLabel: function (compatibility) {
      if (!compatibility) return 'Compatibility pending'
      const labels = {
        central: 'Build anchor',
        synergy: 'Direct synergy',
        conditional_synergy: 'Conditional synergy',
        curated_fit: 'Curated fit',
        role_fit: 'Role complement',
        theme: 'Shared theme',
        theme_only: 'Theme only',
        compatible: 'Compatible',
        redundancy: 'Possible overlap',
        competition: 'Competing triggers',
        state_competition: 'Competing states',
        state_coverage: 'State coverage',
        friction: 'Mechanical friction',
        independent: 'Independent effects',
        indirect: 'Indirect setup',
        anti_synergy: 'Anti-synergy',
        no_effect: 'No mechanical effect',
        duplicate: 'Duplicate effect'
      }
      const kind = labels[compatibility.kind] || 'Contextual fit'
      return `${kind} · ${compatibility.confidenceLabel || compatibility.confidence || 'Inferred'}`
    },
    compatibilityReason: function (compatibility) {
      if (!compatibility) return ''
      const drawback = compatibility.drawbacks && compatibility.drawbacks[0]
      return drawback
        ? `${compatibility.reason} Trade-off: ${drawback}`
        : compatibility.reason
    },
    slotDescription: function (slot) {
      if (slot.slot === 1) return 'Build identity anchor.'
      if (slot.slot === 2) return 'Primary support piece.'
      if (slot.slot === 3) return 'Secondary conversion.'
      return 'Final coverage slot.'
    }
  }
}
</script>

<style lang="scss" scoped>
    @import "../design/main";

    .best-build {
        display: flex;
        flex-direction: column;
        gap: 16px;
        max-width: 1460px;
        margin: 0 auto;
        padding: 20px;
        border: 1px solid rgba(119, 208, 255, 0.12);
        border-radius: 8px;
        background:
            radial-gradient(circle at 15% 0, rgba(119, 208, 255, 0.12), transparent 34%),
            radial-gradient(circle at 86% 20%, rgba(240, 212, 93, 0.08), transparent 30%),
            linear-gradient(180deg, rgba(5, 8, 11, 0.96), rgba(0, 0, 0, 0.9));
        box-shadow: 0 24px 72px rgba(0, 0, 0, 0.45);
        color: $color-text;
    }

    .best-build__header {
        display: grid;
        grid-template-columns: minmax(260px, 0.75fr) minmax(520px, 1fr);
        align-items: stretch;
        gap: 16px;

        h1 {
            font-size: 2.15rem;
            line-height: 1;
            letter-spacing: 0;
            color: #fff;
            text-shadow: 0 2px 16px rgba(119, 208, 255, 0.28);
        }
    }

    .best-build__title {
        min-height: 86px;
        padding: 18px;
        border: 1px solid rgba(119, 208, 255, 0.16);
        border-radius: 6px;
        background:
            linear-gradient(180deg, rgba(13, 20, 24, 0.78), rgba(7, 9, 11, 0.68)),
            rgba(0, 0, 0, 0.26);
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 5px;

        span {
            color: #77d0ff;
            font-size: 0.76rem;
            font-weight: 800;
            letter-spacing: 0;
            text-transform: uppercase;
        }
    }

    .best-build__controls,
    .best-build__mode-switch,
    .best-build__summary,
    .best-build__warnings,
    .best-build__details,
    .best-build__funnel {
        border: 1px solid rgba(255, 255, 255, 0.14);
        border-radius: 6px;
        background: rgba(12, 15, 18, 0.9);
        box-shadow: 0 16px 48px rgba(0, 0, 0, 0.28);
    }

    .best-build__controls {
        display: grid;
        grid-template-columns: minmax(280px, 1fr) auto auto;
        gap: 10px;
        align-items: end;
        min-width: 0;
        padding: 12px;
    }

    .best-build__control {
        display: flex;
        flex-direction: column;
        gap: 6px;
        color: #9da7b3;
        font-size: 0.75rem;
        text-transform: uppercase;

        select {
            min-height: 36px;
            border: 1px solid rgba(119, 208, 255, 0.32);
            border-radius: 4px;
            background: #06080a;
            color: #fff;
            padding: 0 10px;
        }
    }

    .best-build__button {
        min-height: 36px;
        border: 1px solid rgba(255, 255, 255, 0.18);
        border-radius: 4px;
        background: #151a1f;
        color: #fff;
        padding: 0 12px;
        cursor: pointer;

        &:hover {
            border-color: rgba(119, 208, 255, 0.55);
            background: #1d252d;
        }
    }

    .best-build__mode-switch {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 10px;
        padding: 10px;
    }

    .best-build__mode-button {
        display: grid;
        grid-template-rows: auto auto 1fr;
        gap: 4px;
        min-height: 84px;
        border: 1px solid rgba(255, 255, 255, 0.12);
        border-radius: 6px;
        background:
            linear-gradient(180deg, rgba(18, 22, 27, 0.96), rgba(8, 11, 14, 0.96)),
            radial-gradient(circle at 18% 0, rgba(119, 208, 255, 0.14), transparent 42%);
        color: #fff;
        padding: 12px;
        text-align: left;
        cursor: pointer;

        span {
            color: #77d0ff;
            font-size: 0.7rem;
            font-weight: 800;
            text-transform: uppercase;
        }

        strong {
            color: #fff;
            font-size: 1rem;
            line-height: 1.1;
        }

        small {
            color: #aeb9c6;
            font-size: 0.78rem;
            line-height: 1.28;
        }

        &:hover {
            border-color: rgba(119, 208, 255, 0.55);
        }
    }

    .best-build__mode-button--active {
        border-color: rgba(119, 208, 255, 0.84);
        background:
            linear-gradient(180deg, rgba(18, 39, 55, 0.98), rgba(9, 14, 19, 0.98)),
            radial-gradient(circle at 18% 0, rgba(119, 208, 255, 0.24), transparent 46%);
        box-shadow: inset 0 0 0 1px rgba(119, 208, 255, 0.16);
    }

    .best-build__summary {
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(360px, 0.9fr) auto;
        align-items: stretch;
        gap: 12px;
        padding: 14px;
        background:
            linear-gradient(90deg, rgba(119, 208, 255, 0.08), rgba(255, 255, 255, 0.02)),
            rgba(12, 15, 18, 0.9);

        span {
            color: #c9d2dc;
        }
    }

    .best-build__summary-copy {
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 3px;
        min-width: 0;
    }

    .best-build__tradeoffs {
        display: flex;
        flex-wrap: wrap;
        gap: 5px 10px;
        margin-top: 5px;
        font-size: 0.72rem;

        .is-positive {
            color: #9cffb0;
        }

        .is-negative {
            color: #f2bc68;
        }
    }

    .best-build__disclaimer {
        margin-top: 3px;
        color: #7f8b98;
        font-size: 0.68rem;
        line-height: 1.25;
    }

    .best-build__summary-stats {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 8px;

        > span {
            min-width: 0;
            padding: 10px;
            border: 1px solid rgba(119, 208, 255, 0.14);
            border-radius: 5px;
            background: rgba(3, 7, 10, 0.46);
            display: flex;
            flex-direction: column;
            gap: 4px;
        }

        small {
            color: #7f8b98;
            font-size: 0.68rem;
            font-weight: 800;
            text-transform: uppercase;
        }

        strong {
            min-width: 0;
            overflow: hidden;
            color: #ffffff;
            font-size: 0.9rem;
            line-height: 1.15;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }

    .best-build__details {
        display: flex;
        flex-direction: column;
        gap: 12px;
        padding: 14px;
    }

    .best-build__details-header {
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto auto;
        gap: 16px;

        div {
            display: flex;
            flex-direction: column;
            gap: 3px;
            min-width: 0;
        }

        span {
            color: #c9d2dc;
        }
    }

    .best-build__perk-details {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 12px;
    }

    .best-build__perk-detail {
        display: grid;
        grid-template-columns: 72px minmax(0, 1fr);
        gap: 12px;
        min-width: 0;
        border: 1px solid rgba(119, 208, 255, 0.16);
        border-radius: 6px;
        background:
            radial-gradient(circle at 28px 36px, rgba(119, 208, 255, 0.1), transparent 46%),
            rgba(5, 8, 11, 0.78);
        padding: 12px;
    }

    .best-build__perk-detail-icon {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 72px;
        height: 72px;
        overflow: hidden;
        background-image: url(/img/perkBg-md.png);
        background-size: 70px 70px;
        background-position: center;
        background-repeat: no-repeat;

        .sprite-survivor {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) scale(0.52);
            transform-origin: center;
        }
    }

    .best-build__perk-detail-body {
        min-width: 0;

        span {
            display: block;
            margin-bottom: 5px;
            color: #7f8b98;
            font-size: 0.72rem;
            text-transform: uppercase;
        }

        strong {
            display: block;
            margin-bottom: 8px;
            color: #fff;
            line-height: 1.15;
        }

        .best-build__perk-detail-meta {
            display: flex;
            align-items: center;
            gap: 6px;
            margin-bottom: 8px;

            span {
                display: inline-flex;
                margin: 0;
            }

            span:last-child {
                color: #9da7b3;
                font-size: 0.72rem;
                text-transform: uppercase;
            }
        }

        ul {
            display: grid;
            gap: 4px;
            margin: 0;
            padding-left: 16px;
        }

        li {
            color: #c9d2dc;
            font-size: 0.76rem;
            line-height: 1.34;
        }
    }

    .best-build__eyebrow {
        color: #77d0ff !important;
        font-size: 0.72rem;
        font-weight: 800;
        text-transform: uppercase;
    }

    .best-build__source {
        align-self: flex-start;
        border: 1px solid #2d6fa8;
        border-radius: 4px;
        color: #8dc9ff;
        padding: 3px 8px;
        text-transform: uppercase;
        font-size: 0.7rem;
    }

    .best-build__plan-row {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        gap: 10px;
    }

    .best-build__plan-button {
        display: flex;
        flex-direction: column;
        gap: 4px;
        min-height: 70px;
        border: 1px solid rgba(255, 255, 255, 0.12);
        border-radius: 6px;
        background: rgba(15, 18, 22, 0.92);
        color: #fff;
        padding: 10px;
        text-align: left;
        cursor: pointer;

        strong {
            color: #f0d45d;
        }

        span {
            color: #aeb9c6;
            font-size: 0.78rem;
            line-height: 1.25;
        }

        &:hover {
            border-color: rgba(119, 208, 255, 0.55);
        }
    }

    .best-build__plan-button--active {
        border-color: rgba(119, 208, 255, 0.8);
        background: linear-gradient(180deg, rgba(19, 35, 47, 0.96), rgba(14, 17, 22, 0.96));
    }

    .best-build__grid {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 12px;
    }

    .build-slot {
        position: relative;
        display: flex;
        flex-direction: column;
        gap: 10px;
        min-width: 0;
        overflow: hidden;
        border: 1px solid rgba(255, 255, 255, 0.12);
        border-radius: 6px;
        background:
            linear-gradient(180deg, rgba(30, 35, 42, 0.96), rgba(5, 7, 9, 0.97)),
            radial-gradient(circle at 50% 20%, rgba(119, 208, 255, 0.16), transparent 46%);
        padding: 10px;
    }

    .build-slot--central {
        border-color: rgba(240, 212, 93, 0.55);
    }

    .build-slot--locked {
        border-color: rgba(156, 255, 176, 0.58);
    }

    .build-slot__topline,
    .build-slot__meta {
        display: flex;
        justify-content: space-between;
        gap: 8px;
        text-transform: uppercase;
        font-size: 0.72rem;

        span {
            color: #8a93a0;
        }
    }

    .build-slot__topline strong {
        color: #f0d45d;
        text-align: right;
    }

    .build-slot__icon {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        min-height: 172px;
        overflow: hidden;
        background-image: url(/img/perkBg-md.png);
        background-size: 162px 162px;
        background-repeat: no-repeat;
        background-position: center;
    }

    .build-slot__sprite {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 128px;
        height: 128px;
        transform: translate(-50%, -50%);
    }

    .build-slot h2 {
        min-height: 44px;
        font-size: 1rem;
        line-height: 1.15;
        text-align: center;
        text-transform: uppercase;
        word-break: break-word;
    }

    .build-slot p {
        min-height: 42px;
        color: #c9d2dc;
        font-size: 0.78rem;
        line-height: 1.32;
    }

    .build-slot__meta strong {
        color: #9cffb0;
    }

    .build-slot__meta-values,
    .build-slot__alternative-meta {
        display: inline-flex;
        align-items: center;
        justify-content: flex-end;
        gap: 6px;
        min-width: 0;
    }

    .build-slot__tier {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 24px;
        min-height: 18px;
        border: 1px solid rgba(255, 255, 255, 0.14);
        border-radius: 4px;
        background: rgba(255, 255, 255, 0.05);
        color: #c9d2dc;
        font-size: 0.66rem;
        font-weight: 800;
        line-height: 1;
        text-transform: uppercase;
    }

    .build-slot__tier--s {
        border-color: rgba(255, 213, 88, 0.72);
        color: #ffd558;
    }

    .build-slot__tier--a {
        border-color: rgba(156, 255, 176, 0.6);
        color: #9cffb0;
    }

    .build-slot__tier--b {
        border-color: rgba(119, 208, 255, 0.58);
        color: #77d0ff;
    }

    .build-slot__tier--c {
        border-color: rgba(190, 198, 208, 0.36);
        color: #bec6d0;
    }

    .build-slot__tier--d {
        border-color: rgba(242, 188, 104, 0.48);
        color: #f2bc68;
    }

    .build-slot__tier--f {
        border-color: rgba(255, 112, 112, 0.48);
        color: #ff7070;
    }

    .build-slot__actions {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .build-slot__search {
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        gap: 5px 8px;
        align-items: center;

        span {
            color: #8a93a0;
            font-size: 0.68rem;
            font-weight: 800;
            text-transform: uppercase;
        }

        small {
            color: #77d0ff;
            font-size: 0.68rem;
            text-align: right;
        }

        input {
            grid-column: 1 / -1;
            min-width: 0;
            min-height: 32px;
            border: 1px solid rgba(119, 208, 255, 0.22);
            border-radius: 4px;
            background: #060a0d;
            color: #fff;
            padding: 0 9px;

            &::placeholder {
                color: #62707d;
            }

            &:focus {
                outline: none;
                border-color: rgba(119, 208, 255, 0.72);
                box-shadow: 0 0 0 1px rgba(119, 208, 255, 0.16);
            }
        }
    }

    .build-slot__lock-button {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 32px;
        border: 1px solid rgba(255, 255, 255, 0.16);
        border-radius: 4px;
        background: #11171c;
        color: #d8dee6;
        font-size: 0.8rem;
        cursor: pointer;
    }

    .build-slot__lock-button--active {
        border-color: rgba(156, 255, 176, 0.55);
        color: #9cffb0;
    }

    .build-slot__alternatives {
        display: grid;
        gap: 6px;

        button {
            display: grid;
            grid-template-columns: minmax(0, 1fr) auto;
            gap: 8px;
            align-items: center;
            min-height: 34px;
            border: 1px solid rgba(119, 208, 255, 0.18);
            border-radius: 4px;
            background: rgba(9, 13, 17, 0.92);
            color: #e8edf4;
            padding: 0 8px;
            text-align: left;
            cursor: pointer;

            > span:first-child {
                min-width: 0;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            strong {
                color: #9cffb0;
            }

            .build-slot__score--zero {
                color: #62707d;
            }

            &:hover {
                border-color: rgba(119, 208, 255, 0.62);
                background: rgba(17, 26, 35, 0.95);
            }
        }
    }

    .build-slot__alternative-copy {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;

        > span {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        small {
            overflow: hidden;
            color: #7f8b98;
            font-size: 0.62rem;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }

    .build-slot__alternatives--scroll {
        max-height: 190px;
        overflow-y: auto;
        padding-right: 3px;
        scrollbar-color: rgba(119, 208, 255, 0.45) rgba(255, 255, 255, 0.05);

        button {
            min-height: 32px;
        }
    }

    .build-slot__empty-alternatives {
        min-height: 34px;
        border: 1px dashed rgba(255, 255, 255, 0.14);
        border-radius: 4px;
        color: #7f8b98;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.78rem;
    }

    .best-build__warnings {
        padding: 12px;
        border-color: rgba(240, 212, 93, 0.5);

        ul {
            margin-top: 8px;
            padding-left: 18px;
            color: #f2dc81;
        }
    }

    .best-build__funnel {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 0;
        padding: 10px;

        div {
            position: relative;
            display: flex;
            flex-direction: column;
            gap: 3px;
            min-width: 0;
            padding: 0 18px 0 10px;

            &:not(:last-child)::after {
                content: ">";
                position: absolute;
                top: 50%;
                right: 3px;
                transform: translateY(-50%);
                color: #77d0ff;
                font-weight: 800;
            }
        }

        span {
            color: #7f8b98;
            font-size: 0.72rem;
            text-transform: uppercase;
        }

        strong {
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }

    @media screen and (max-width: 1180px) {
        .best-build__summary {
            grid-template-columns: 1fr;
        }

        .best-build__summary-stats {
            grid-template-columns: repeat(4, minmax(0, 1fr));
        }

        .best-build__perk-details {
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }
    }

    @media screen and (max-width: 980px) {
        .best-build__header {
            align-items: stretch;
            grid-template-columns: 1fr;
        }

        .best-build__controls {
            min-width: 0;
        }

        .best-build__controls,
        .best-build__mode-switch,
        .best-build__grid,
        .best-build__funnel {
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }
    }

    @media screen and (max-width: 620px) {
        .best-build__controls,
        .best-build__mode-switch,
        .best-build__grid,
        .best-build__funnel,
        .best-build__perk-details,
        .best-build__summary-stats,
        .best-build__summary,
        .best-build__details {
            grid-template-columns: 1fr;
            flex-direction: column;
        }

        .best-build__perk-detail {
            grid-template-columns: 62px minmax(0, 1fr);
        }

        .best-build__perk-detail-icon {
            width: 62px;
            height: 62px;
            background-size: 60px 60px;

            .sprite-survivor {
                transform: translate(-50%, -50%) scale(0.46);
            }
        }

        .best-build__details-header {
            grid-template-columns: 1fr;
        }

        .best-build__funnel div:not(:last-child)::after {
            display: none;
        }
    }
</style>
